/**
 * Préférence d'apparence UI : clair / sombre / selon le navigateur.
 * Clé localStorage `theme` : 'light' | 'dark' | 'system'.
 * - light|dark → data-theme sur <html>
 * - system → pas de data-theme (CSS @media prefers-color-scheme)
 */

export type ThemePreference = 'light' | 'dark' | 'system'

export const THEME_STORAGE_KEY = 'theme'
export const THEME_CHANGE_EVENT = 'theme-change'

/** Cookie partagé avec api.* (pages allauth OAuth hors SPA). */
function syncThemeCookie(preference: ThemePreference): void {
  if (typeof document === 'undefined') return
  try {
    const parts = [
      `${THEME_STORAGE_KEY}=${encodeURIComponent(preference)}`,
      'path=/',
      'max-age=31536000',
      'SameSite=Lax',
    ]
    const host = window.location.hostname
    if (host !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(host) && host.includes('.')) {
      const segs = host.split('.')
      if (segs.length >= 2) parts.push(`domain=.${segs.slice(-2).join('.')}`)
    }
    document.cookie = parts.join('; ')
  } catch {
    /* ignore */
  }
}

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    /* ignore */
  }
  return 'system'
}

export function applyThemePreference(preference: ThemePreference): void {
  const root = document.documentElement
  if (preference === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.dataset.theme = preference
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    /* ignore */
  }
  syncThemeCookie(preference)
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: preference }))
}

export function resolvedColorScheme(preference: ThemePreference = readThemePreference()): 'light' | 'dark' {
  if (preference === 'light' || preference === 'dark') return preference
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}
